import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { createChart, CrosshairMode, LineStyle } from "lightweight-charts";
import { ExternalLink, RefreshCw } from "lucide-react";

// 2026-10-30 — chat: "Need to integrate charts (with one or two indicators)... free"
// -> "build it with EMA 9/21 and RSI". TradingView Lightweight Charts (Apache-2.0,
// attribution link below) fed by /kite-console/candles (Kite historical candles for
// the exact contract; EMA 9/21 + Wilder RSI 14 computed in trade_terminal.py).
// Price lines: your average price in the selected account + working order prices.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERVALS = ["1", "3", "5", "15"];
const C = {
  bull: "#26a69a", bear: "#ef5350", ema9: "#f5b041", ema21: "#5dade2", rsi: "#af7ac5",
  grid: "rgba(255,255,255,0.06)", text: "rgba(242,237,228,0.65)",
};
const POLL_MS = 15000;

const baseOptions = (height) => ({
  height,
  layout: { background: { color: "transparent" }, textColor: C.text, fontSize: 10 },
  grid: { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
  crosshair: { mode: CrosshairMode.Normal },
  rightPriceScale: { borderColor: C.grid, minimumWidth: 64 },     // same width on both panes so bars line up
  timeScale: { borderColor: C.grid, timeVisible: true, secondsVisible: false },
  handleScale: true, handleScroll: true,
});

export const PriceChart = ({ exchange, tradingsymbol, avgPrice, orderPrices = [], height = 320 }) => {
  const priceEl = useRef(null);
  const rsiEl = useRef(null);
  const s = useRef({});
  const lines = useRef([]);
  const [iv, setIv] = useState("3");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const fitted = useRef("");

  // build both charts once
  useEffect(() => {
    const price = createChart(priceEl.current, { ...baseOptions(height), width: priceEl.current.clientWidth });
    const rsi = createChart(rsiEl.current, { ...baseOptions(110), width: rsiEl.current.clientWidth,
                                             layout: { ...baseOptions(110).layout, attributionLogo: false } });
    const candles = price.addCandlestickSeries({ upColor: C.bull, downColor: C.bear, borderVisible: false,
                                                 wickUpColor: C.bull, wickDownColor: C.bear });
    const vol = price.addHistogramSeries({ priceScaleId: "vol", priceFormat: { type: "volume" }, lastValueVisible: false,
                                           priceLineVisible: false });
    price.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    const ema9 = price.addLineSeries({ color: C.ema9, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    const ema21 = price.addLineSeries({ color: C.ema21, lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    const rsiLine = rsi.addLineSeries({ color: C.rsi, lineWidth: 1, priceLineVisible: false,
                                        autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }) });
    rsi.priceScale("right").applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } });
    [70, 30].forEach((v) => rsiLine.createPriceLine({ price: v, color: "rgba(255,255,255,0.25)", lineWidth: 1,
                                                       lineStyle: LineStyle.Dashed, axisLabelVisible: false }));
    // keep the two time axes in step
    let syncing = false;
    const sync = (to) => (r) => {
      if (syncing || !r) return;
      syncing = true;
      to.timeScale().setVisibleLogicalRange(r);
      syncing = false;
    };
    price.timeScale().subscribeVisibleLogicalRangeChange(sync(rsi));
    rsi.timeScale().subscribeVisibleLogicalRangeChange(sync(price));
    const ro = new ResizeObserver(() => {
      price.applyOptions({ width: priceEl.current?.clientWidth || 300 });
      rsi.applyOptions({ width: rsiEl.current?.clientWidth || 300 });
    });
    ro.observe(priceEl.current);
    s.current = { price, rsi, candles, vol, ema9, ema21, rsiLine };
    return () => { ro.disconnect(); price.remove(); rsi.remove(); s.current = {}; };
  }, [height]);

  // load + poll candles
  useEffect(() => {
    if (!tradingsymbol) return undefined;
    let alive = true;
    const load = async (first) => {
      if (!first && document.visibilityState !== "visible") return;     // poll only while the tab is visible
      setBusy(true);
      try {
        const { data: d } = await axios.get(`${API}/kite-console/candles`,
                                            { params: { exchange, tradingsymbol, interval: iv } });
        if (alive) { setData(d); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Chart data unavailable");
      } finally { if (alive) setBusy(false); }
    };
    setData(null);
    load(true);
    const id = setInterval(() => load(false), POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [exchange, tradingsymbol, iv]);

  // push data into the series
  useEffect(() => {
    const x = s.current;
    if (!x.candles || !data) return;
    x.candles.setData(data.candles || []);
    x.vol.setData((data.volume || []).map((v) => ({ time: v.time, value: v.value,
                                                     color: v.up ? "rgba(38,166,154,0.35)" : "rgba(239,83,80,0.35)" })));
    x.ema9.setData(data.ema9 || []);
    x.ema21.setData(data.ema21 || []);
    // RSI on the SAME time points as the candles (whitespace where it isn't defined yet),
    // so the two panes' logical ranges — and the bars — line up exactly
    const rsiAt = new Map((data.rsi || []).map((r) => [r.time, r.value]));
    x.rsiLine.setData((data.candles || []).map((c) => (rsiAt.has(c.time) ? { time: c.time, value: rsiAt.get(c.time) } : { time: c.time })));
    const key = `${tradingsymbol}|${iv}`;
    if (fitted.current !== key && (data.candles || []).length) {      // fit once per contract/interval, keep user zoom after
      fitted.current = key;
      const n = data.candles.length;
      const r = { from: Math.max(0, n - 120), to: n + 3 };
      x.price.timeScale().setVisibleLogicalRange(r);
      x.rsi.timeScale().setVisibleLogicalRange(r);
    }
  }, [data, tradingsymbol, iv]);

  // average-price and working-order lines
  useEffect(() => {
    const x = s.current;
    if (!x.candles) return;
    lines.current.forEach((l) => { try { x.candles.removePriceLine(l); } catch { /* gone */ } });
    lines.current = [];
    if (avgPrice > 0) {
      lines.current.push(x.candles.createPriceLine({ price: Number(avgPrice), color: "#f2ede4", lineWidth: 1,
                                                      lineStyle: LineStyle.Dashed, title: "avg" }));
    }
    orderPrices.filter((o) => o.price > 0).forEach((o) => {
      lines.current.push(x.candles.createPriceLine({ price: Number(o.price), lineWidth: 1, lineStyle: LineStyle.Dotted,
                                                      color: o.side === "BUY" ? C.bull : C.bear, title: o.side.toLowerCase() }));
    });
  }, [avgPrice, JSON.stringify(orderPrices), data]); // eslint-disable-line react-hooks/exhaustive-deps

  const last = data?.last;
  const kiteUrl = data?.instrument_token
    ? `https://kite.zerodha.com/chart/web/tvc/${exchange}/${encodeURIComponent(tradingsymbol)}/${data.instrument_token}` : null;
  return (
    <div className="border border-grid p-3 space-y-2" data-testid="mo-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 font-mono-data text-[10px]">
        <div className="flex items-center gap-1">
          {INTERVALS.map((v) => (
            <button key={v} onClick={() => setIv(v)} className="px-2 py-0.5 border"
                    style={{ borderColor: iv === v ? "var(--bull)" : "var(--grid)", color: iv === v ? "var(--bull)" : undefined }}>
              {v}m
            </button>
          ))}
          {busy && <RefreshCw size={11} className="animate-spin text-muted-3 ml-1" />}
        </div>
        <div className="flex items-center gap-3 text-muted-2">
          <span><span style={{ color: C.ema9 }}>EMA 9</span> {last?.ema9 ?? "—"}</span>
          <span><span style={{ color: C.ema21 }}>EMA 21</span> {last?.ema21 ?? "—"}</span>
          <span><span style={{ color: C.rsi }}>RSI 14</span> {last?.rsi ?? "—"}</span>
          {kiteUrl && (
            <a href={kiteUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline text-muted-3">
              Open in Kite <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
      {err && <div className="text-[11px] font-mono-data" style={{ color: "var(--bear)" }}>{err}</div>}
      <div ref={priceEl} className="w-full" />
      <div ref={rsiEl} className="w-full border-t border-grid/60" />
      <div className="flex flex-wrap justify-between gap-2 text-[9px] font-mono-data text-muted-3">
        <span>Kite candles · IST · last 2 sessions · refreshes every 15 s · gaps = no trades in that minute</span>
        <a href="https://www.tradingview.com/" target="_blank" rel="noreferrer" className="underline">Charts by TradingView</a>
      </div>
    </div>
  );
};

export default PriceChart;
